export interface ContactValidationError {
  field: 'name' | 'phone' | 'email';
  type: 'missing' | 'invalid_format' | 'duplicate';
  message: string;
  count?: number;
}

export type ContactSeverity = 'valid' | 'warning' | 'critical';

export interface ValidatedContact {
  rowIndex: number;
  data: Record<string, string>;
  errors: ContactValidationError[];
  severity: ContactSeverity;
}

export interface CsvValidationStats {
  total: number;
  valid: number;
  warnings: number;
  critical: number;
}

export interface CsvValidationResult {
  contacts: ValidatedContact[];
  stats: CsvValidationStats;
}

// Phone regex ported from v1 — supports international, parentheses, simple formats + extensions
const PHONE_REGEX =
  /^\+?\d{1,3}[\s.-]?\d{2}[\s.-]?\d{4}[\s.-]?\d{4}(?:\s*(?:ext|EXT|Ext)\.?\s*\d{1,5})?$|^\+?\d{1,3}[\s.-]?\d{3}[\s.-]?\d{3}[\s.-]?\d{4}(?:\s*(?:ext|EXT|Ext)\.?\s*\d{1,5})?$|^\(\+?\d{1,3}\)[\s.-]?\d{3}[-.\s]?\d{3}[-.\s]?\d{4}(?:\s*(?:ext|EXT|Ext)\.?\s*\d+)?$|^\(\d{3}\)[\s.-]?\d{3}[-.\s]?\d{4}(?:\s*(?:ext|EXT|Ext)\.?\s*\d+)?$|^\d{3}[\s.-]?\d{3}[\s.-]?\d{4}$/;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function countOccurrences (values: string[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const v of values) {
    const trimmed = v.trim();
    if (trimmed !== '') {
      counts[trimmed] = (counts[trimmed] ?? 0) + 1;
    }
  }
  return counts;
}

function determineSeverity (errors: ContactValidationError[]): ContactSeverity {
  if (errors.some((e) => e.type === 'missing')) {
    return 'critical';
  }
  if (errors.length > 0) {
    return 'warning';
  }
  return 'valid';
}

export function validateCsvContacts (rows: Array<Record<string, string>>): CsvValidationResult {
  const names = rows.map((r) => r.name ?? '');
  const phones = rows.map((r) => r.phone ?? '');

  const nameOccurrences = countOccurrences(names);
  const phoneOccurrences = countOccurrences(phones);

  const contacts: ValidatedContact[] = rows.map((row, index) => {
    const errors: ContactValidationError[] = [];
    const name = (row.name ?? '').trim();
    const phone = (row.phone ?? '').trim();
    const email = (row.email ?? '').trim();

    // Critical: missing required fields
    if (name === '') {
      errors.push({ field: 'name', type: 'missing', message: 'missing' });
    }

    if (phone === '') {
      errors.push({ field: 'phone', type: 'missing', message: 'missing' });
    }

    // Warning: invalid formats
    if (phone !== '' && !PHONE_REGEX.test(phone)) {
      errors.push({ field: 'phone', type: 'invalid_format', message: 'invalid' });
    }

    if (email !== '' && !EMAIL_REGEX.test(email)) {
      errors.push({ field: 'email', type: 'invalid_format', message: 'invalid' });
    }

    // Warning: duplicates
    if (name !== '' && nameOccurrences[name] > 1) {
      errors.push({
        field: 'name',
        type: 'duplicate',
        message: 'duplicate',
        count: nameOccurrences[name],
      });
    }

    if (phone !== '' && phoneOccurrences[phone] > 1) {
      errors.push({
        field: 'phone',
        type: 'duplicate',
        message: 'duplicate',
        count: phoneOccurrences[phone],
      });
    }

    return {
      rowIndex: index,
      data: row,
      errors,
      severity: determineSeverity(errors),
    };
  });

  // Sort: critical first, then warnings, then valid
  const severityOrder: Record<ContactSeverity, number> = { critical: 0, warning: 1, valid: 2 };
  contacts.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  const stats: CsvValidationStats = {
    total: contacts.length,
    valid: contacts.filter((c) => c.severity === 'valid').length,
    warnings: contacts.filter((c) => c.severity === 'warning').length,
    critical: contacts.filter((c) => c.severity === 'critical').length,
  };

  return { contacts, stats };
}

/**
 * Recalculates validation after removing a contact.
 * Duplicate counts may change when a contact is removed.
 */
export function revalidateAfterRemoval (
  contacts: ValidatedContact[],
): CsvValidationResult {
  const rows = contacts.map((c) => c.data);
  return validateCsvContacts(rows);
}
